import { useCallback, useEffect, useRef, useState } from "react";
import type { ClipboardEvent, ChangeEvent } from "react";
import { useActiveTenantName } from "../../../hooks/useTenantContext";
import { apiService } from "../../../services/api";
import { getApiErrorMessage } from "../../../services/apiErrors";
import { notify } from "../../../services/notify";
import { FEEDBACK_COPY } from "./FeedbackDialog.copy";
import type { FeedbackKind } from "./FeedbackDialog.copy";
import type { FeedbackScreenshotState } from "./FeedbackDialog.types";

export const FEEDBACK_ENDPOINT = "/api/v1/feedback";
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export function useFeedbackDialogInternalState(open: boolean, onClose: () => void) {
  const workspace = useActiveTenantName();
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<FeedbackScreenshotState | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [includeDetails, setIncludeDetails] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const clearScreenshot = useCallback(() => {
    setScreenshot((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  // A fresh dialog each time it opens.
  useEffect(() => {
    if (open) return;
    setKind("bug");
    setMessage("");
    setIncludeDetails(true);
    setBusy(false);
    setError(null);
    setTouched(false);
    setScreenshotError(null);
    clearScreenshot();
  }, [open, clearScreenshot]);

  const takeFile = useCallback((file: File | undefined | null) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) return setScreenshotError(FEEDBACK_COPY.screenshotType);
    if (file.size > MAX_BYTES) return setScreenshotError(FEEDBACK_COPY.screenshotSize);
    setScreenshotError(null);
    setScreenshot((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return { file, url: URL.createObjectURL(file) };
    });
  }, []);

  const onPickFile = (event: ChangeEvent<HTMLInputElement>) => {
    takeFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const onPaste = (event: ClipboardEvent<HTMLElement>) => {
    const image = Array.from(event.clipboardData?.items ?? []).find((item) => item.kind === "file" && item.type.startsWith("image/"));
    const file = image?.getAsFile();
    if (!file) return;
    event.preventDefault();
    takeFile(file);
  };

  const trimmed = message.trim();
  const messageError = touched && !trimmed ? FEEDBACK_COPY.messageRequired : undefined;

  const submit = async () => {
    setTouched(true);
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append("kind", kind);
    form.append("message", trimmed);
    if (includeDetails) {
      form.append("page", `${window.location.pathname}${window.location.search}`.slice(0, 500));
      form.append("browser", navigator.userAgent.slice(0, 300));
      if (workspace) form.append("workspace", workspace.slice(0, 200));
    }
    if (screenshot) form.append("screenshot", screenshot.file);
    try {
      await apiService.postFormData(FEEDBACK_ENDPOINT, form);
      notify({ kind: "success", title: FEEDBACK_COPY.sentTitle, body: FEEDBACK_COPY.sentBody, icon: "send" });
      setBusy(false);
      onClose();
    } catch (failure) {
      setError(getApiErrorMessage(failure, FEEDBACK_COPY.fallbackError));
      setBusy(false);
    }
  };

  return {
    kind,
    setKind,
    message,
    setMessage,
    messageError,
    screenshot,
    screenshotError,
    clearScreenshot,
    includeDetails,
    setIncludeDetails,
    busy,
    error,
    submit,
    fileRef,
    onPickFile,
    onPaste,
  };
}
